<template>
  <div class="login-container">
    <div class="tech-bg-glow"></div>

    <div class="language-switcher">
      <el-dropdown @command="handleLanguageChange" trigger="click">
        <span class="el-dropdown-link tech-link">
          {{ $t('language.switch') }}<i class="el-icon-arrow-down el-icon--right"></i>
        </span>
        <el-dropdown-menu slot="dropdown" class="tech-dropdown-menu">
          <el-dropdown-item command="en">{{ $t('language.en') }}</el-dropdown-item>
          <el-dropdown-item command="zh">{{ $t('language.zh') }}</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <el-form :model="loginForm" :rules="loginRules" ref="loginForm" label-width="0px" class="login-form tech-card">

      <div class="login-brand-header">
        <div class="brand-row">
          <img :src="require('@/assets/logo.png')" class="brand-logo tech-logo-filter" alt="Logo" />
          <div class="brand-name">Proclink</div>
        </div>
        <div class="brand-slogan">AI BUSINESS ASSISTANT</div>
      </div>

      <el-form-item prop="username">
        <el-input
            v-model="loginForm.username"
            :placeholder="$t('login.username')"
            :disabled="loading"
            prefix-icon="el-icon-user"
            class="tech-input-login"
        ></el-input>
      </el-form-item>

      <el-form-item prop="password">
        <el-input
            type="password"
            v-model="loginForm.password"
            :placeholder="$t('login.password')"
            :disabled="loading"
            prefix-icon="el-icon-lock"
            show-password
            class="tech-input-login"
        ></el-input>
      </el-form-item>

      <el-form-item prop="captcha">
        <div class="captcha-container">
          <el-input
              v-model="loginForm.captcha"
              :placeholder="$t('login.captcha')"
              :disabled="loading"
              class="captcha-input tech-input-login"
              prefix-icon="el-icon-key"
              @keyup.enter.native="handleLogin"
          ></el-input>
          <canvas
              ref="captchaCanvas"
              class="captcha-display tech-captcha-canvas"
              :title="$t('login.captcha')"
              width="100"
              height="40"
              @click="refreshCaptcha"
          ></canvas>
        </div>
      </el-form-item>

      <el-form-item>
        <el-button
            type="primary"
            style="width:100%;"
            @click="handleLogin"
            :loading="loading"
            :disabled="loading"
            class="tech-login-btn"
        >
          {{ loading ? $t('login.loggingIn') : $t('login.button') }}
        </el-button>
      </el-form-item>

      <div class="register-tip">
        {{ $t('login.noAccountYet') }}
        <a :href="registerLink" target="_blank" class="register-link tech-link-highlight">{{ $t('login.registerNow') }}</a>
      </div>
    </el-form>
  </div>
</template>

<script>
import languageMixin from '../mixins/languageMixin';
import api from '../api';

const HttpStatus = {
  SUCCESS: 200,
  UNAUTHORIZED: 401,
};

export default {
  name: 'LoginPage',
  mixins: [languageMixin],
  data() {
    const validateCaptcha = (rule, value, callback) => {
      if (!value) {
        callback(new Error(this.$t('login.validation.captchaRequired')));
      } else if (value.toLowerCase() !== this.captchaText.toLowerCase()) {
        callback(new Error(this.$t('login.validation.captchaIncorrect')));
      } else {
        callback();
      }
    };

    return {
      loginForm: {
        username: '',
        password: '',
        captcha: ''
      },
      captchaText: '',
      loginRules: {
        username: [{required: true, trigger: 'blur', message: this.$t('login.validation.usernameRequired')}],
        password: [{required: true, trigger: 'blur', message: this.$t('login.validation.passwordRequired')}],
        captcha: [{required: true, trigger: 'blur', validator: validateCaptcha}]
      },
      loading: false,
      registerLink: 'https://shop.proclink.net/account/sign-up.html'
    };
  },
  mounted() {
    this.refreshCaptcha();
  },
  methods: {
    refreshCaptcha() {
      const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
      let newCaptchaText = '';
      for (let i = 0; i < 4; i++) {
        newCaptchaText += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.captchaText = newCaptchaText;
      this.loginForm.captcha = '';

      const canvas = this.$refs.captchaCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      // Canvas 绘图逻辑：蓝色系
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(0, 50, 100, 0.2)';
      ctx.fillRect(0, 0, width, height);

      // 干扰线
      ctx.lineWidth = 1;
      for (let i = 0; i < 5; i++) {
        ctx.strokeStyle = this.getBlueColor();
        ctx.beginPath();
        ctx.moveTo(Math.random() * width, Math.random() * height);
        ctx.lineTo(Math.random() * width, Math.random() * height);
        ctx.stroke();
      }

      // 噪点
      for (let i = 0; i < 30; i++) {
        ctx.fillStyle = this.getBlueColor();
        ctx.beginPath();
        ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
        ctx.fill();
      }

      // 文字
      const charWidth = width / 5;
      for (let i = 0; i < this.captchaText.length; i++) {
        const char = this.captchaText[i];
        const fontSize = Math.floor(Math.random() * 8 + 24);
        const angle = Math.random() * 0.5 - 0.25;
        const x = charWidth * (i + 0.5);
        const y = height / 2 + Math.random() * 10 - 5;

        ctx.save();
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.shadowColor = "rgba(0, 153, 255, 0.9)";
        ctx.shadowBlur = 5;

        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(char, 0, 0);
        ctx.restore();
      }
    },

    getBlueColor() {
      const r = Math.floor(Math.random() * 50);
      const g = Math.floor(Math.random() * 155 + 100);
      const b = Math.floor(Math.random() * 55 + 200);
      return `rgb(${r},${g},${b})`;
    },

    handleLanguageChange(command) {
      this.switchLanguage(command);
      this.loginRules.username[0].message = this.$t('login.validation.usernameRequired');
      this.loginRules.password[0].message = this.$t('login.validation.passwordRequired');

      const validateCaptcha = (rule, value, callback) => {
        if (!value) {
          callback(new Error(this.$t('login.validation.captchaRequired')));
        } else if (value.toLowerCase() !== this.captchaText.toLowerCase()) {
          callback(new Error(this.$t('login.validation.captchaIncorrect')));
        } else {
          callback();
        }
      };
      this.loginRules.captcha = [{required: true, trigger: 'blur', validator: validateCaptcha}];

      this.$forceUpdate();
      this.$refs.loginForm.clearValidate();
      this.$nextTick(() => {
        this.refreshCaptcha();
      });
    },

    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true
          api.login(this.loginForm.username, this.loginForm.password)
              .then(response => {
                const res = response.data;
                if (res.code === HttpStatus.SUCCESS) {
                  this.$message({
                    message: res.msg || this.$t('login.successMessage'),
                    type: 'success'
                  });
                  localStorage.setItem('isLoggedIn', 'true');
                  if (res.data) {
                    localStorage.setItem('token', res.data.token);
                    localStorage.setItem('userId', res.data.userId);
                    localStorage.setItem('email', res.data.email);
                  } else {
                    localStorage.setItem('email', this.loginForm.username);
                  }
                  this.$router.push('/chat');
                } else {
                  this.$message({ message: res.msg || 'Login failed', type: 'error' });
                }
              })
              .catch(error => {
                console.error('Login API error:', error);
                if (!(error.response && error.response.status === 401)) {
                  this.$message({ message: 'Login request failed.', type: 'error' });
                }
              })
              .finally(() => {
                this.loading = false
                this.refreshCaptcha();
              });
        } else {
          this.refreshCaptcha();
          return false;
        }
      });
    }
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  position: relative;
}

.tech-bg-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(0, 100, 255, 0.15) 0%, transparent 70%);
  z-index: 0;
  pointer-events: none;
}

.language-switcher {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.tech-link {
  color: var(--tech-text-sub);
  transition: all 0.3s;
}
.tech-link:hover {
  color: var(--tech-primary);
  text-shadow: 0 0 5px var(--tech-primary);
}

.login-form {
  width: 400px;
  padding: 40px 30px;
  background: rgba(10, 20, 35, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 153, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.6), inset 0 0 20px rgba(0, 153, 255, 0.05);
  z-index: 1;
}

/* --- [新增] 品牌头部样式 --- */
.login-brand-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px; /* 替代原有 title 的 margin */
}

.brand-row {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
}

.brand-logo {
  height: 32px; /* 登录页稍微大一点 */
  width: auto;
  margin-right: 10px;
}

/* 核心滤镜：黑转白+蓝光 */
.tech-logo-filter {
  filter: brightness(0) invert(1) drop-shadow(0 0 2px rgba(0, 153, 255, 0.5));
}

.brand-name {
  font-size: 24px; /* 登录页字号加大 */
  font-weight: 700;
  color: var(--tech-text-main);
  letter-spacing: 1px;
  font-family: 'Segoe UI', sans-serif;
  line-height: 1;
}

.brand-slogan {
  font-size: 10px;
  color: var(--tech-primary);
  letter-spacing: 2px;
  font-weight: 500;
  text-align: center;
}

/* Input Styles */
.tech-input-login >>> .el-input__inner {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: none !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 0 !important;
  color: #fff !important;
  padding-left: 35px;
  transition: all 0.3s;
}

.tech-input-login >>> .el-input__inner:focus {
  border-bottom-color: var(--tech-primary) !important;
  background-color: rgba(0, 153, 255, 0.05) !important;
  box-shadow: 0 5px 15px -8px rgba(0, 153, 255, 0.4);
}

.tech-input-login >>> .el-input__prefix {
  color: var(--tech-text-sub);
}

/* Captcha */
.captcha-container {
  display: flex;
  align-items: center;
  width: 100%;
}
.captcha-input { flex-grow: 1; margin-right: 15px; }

.tech-captcha-canvas {
  flex-shrink: 0;
  width: 100px;
  height: 40px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid rgba(0, 153, 255, 0.2);
  transition: all 0.3s;
}
.tech-captcha-canvas:hover {
  border-color: var(--tech-primary);
  box-shadow: 0 0 10px rgba(0, 153, 255, 0.2);
}

/* Login Button */
.tech-login-btn {
  background: linear-gradient(90deg, #0055aa 0%, var(--tech-primary) 100%) !important;
  border: none !important;
  color: #fff !important;
  font-weight: bold;
  letter-spacing: 1px;
  margin-top: 10px;
  height: 40px;
  box-shadow: 0 5px 15px rgba(0, 80, 200, 0.3);
  transition: all 0.3s;
}
.tech-login-btn:hover {
  opacity: 0.95;
  box-shadow: 0 0 20px rgba(0, 153, 255, 0.5);
  transform: translateY(-1px);
}

.register-tip {
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
  color: var(--tech-text-sub);
}

.tech-link-highlight {
  color: var(--tech-primary);
  text-decoration: none;
  margin-left: 5px;
  transition: all 0.3s;
}
.tech-link-highlight:hover {
  text-shadow: 0 0 8px var(--tech-primary);
}

@media (max-width: 480px) {
  .login-form {
    width: 90%;
    padding: 30px 20px;
  }
}
</style>